import { makeActionNode } from "./constant";
import { ActionNode, makeScheduledConfig, PRESET_COLD_ID, PRESET_WARM_ID, TriggerNode } from "./types";

// Feedback Collection — full design from the 2026-06-24 mockups:
//
//   Set Delay Time (30 min after event)
//        ↓
//   Send Message: feedback_request  (Good / Average / Poor buttons, waits 24h)
//        ↓
//   Condition reply contains "good"?
//   ├─ TRUE  → Send feedback_thanks_good ("Thank you so much!...")
//   │         → Store Data Feedback == Good → contact_profile → END
//   └─ FALSE → Condition reply contains "average"?
//              ├─ TRUE  → Send feedback_suggestions ("What can we do to improve?")
//              │         → Store Data Feedback == Average → contact_profile → END
//              └─ FALSE (Poor) → Store Data Feedback == Poor → contact_profile
//                                → Create Support Ticket (priority: high)
//                                → Send Internal Notification (Support Team,
//                                   feedback_internal_escalation template)
//                                → Assign Agent → END
//
// Each path's UX:
//   Good     → thank you + store, bot done (customer hears "great, you're happy")
//   Average  → ask "what can we improve" + store, bot done (open feedback)
//   Poor     → ask "what can we improve" (same reply as Average) + store +
//              create ticket + ping support team + hand to agent
//
// 4 Meta templates the preset depends on (all submitted):
//   feedback_request                 3 buttons Good/Average/Poor  (PENDING → ~24h)
//   feedback_thanks_good             body only                    (PENDING)
//   feedback_suggestions             body only                    (PENDING)
//   feedback_internal_escalation     body only, internal team     (PENDING)
//
// Backend gaps that block end-to-end (flagged for Ankit):
//   - store_data step type → no handler in temporal_worker/workflows.py
//     (should write to convohub.contacts.attributes or a new feedback table)
//   - create_support_ticket → no handler (should POST to convohub /tickets)
//   - send_internal_notification → no handler (should send template to a
//     team WhatsApp number configured per-tenant)
//
// Once the backend handlers ship + the 4 templates approve, this preset
// runs end-to-end and the customer profile page (per screenshot) shows
// the auto-created tickets under the "Tickets" tab.
// Missed Appointment — separate journey, Scree 2026-06-24 spec.
//
//   Trigger: On Event "Appointment_Missed" — fires 24h after appointment_time
//   if the customer never reached APPOINTMENT CONFIRMED status. The
//   external booking system (or an internal scheduler) POSTs the event;
//   Temporal handles the timer side per Joseph's call ("use Temporal for
//   timer").
//
//   Send appointment_missed   (Reschedule / Cancel buttons, waits 24h)
//        ↓
//   Condition reply contains "reschedule"?
//   ├─ TRUE  → Send appointment_reschedule  (Tomorrow / This week / Next week, waits)
//   │         → Assign Agent
//   │         → Tag RESCHEDULE REQUESTED
//   └─ FALSE (Cancel) → Send appointment_cancel_farewell
//                       ("Your appointment has been cancelled as requested...
//                         Thank you for choosing us.")
//                     → Assign Agent
//                     → Tag APPOINTMENT CANCELLED
//
// 4 Meta templates used (all UTILITY):
//   appointment_missed              PENDING  Reschedule / Cancel buttons
//   appointment_reschedule          APPROVED  Tomorrow/This wk/Next wk
//   appointment_cancel_farewell     PENDING  goodbye + door-open message
//
// Once Meta approves appointment_missed + appointment_cancel_farewell
// (~24h), and the booking system fires Appointment_Missed events,
// this journey runs end-to-end. Until then the journey definition
// works in the canvas but waits on those 2 template approvals.
export const makeMissedAppointmentJourneyActions = (): ActionNode[] => [
  // step 0 (preset_1) — Send appointment_missed template. Waits 24h
  // for the customer's button click.
  makeActionNode(
    "WhatsApp Message",
    {
      channel: "WhatsApp",
      templateId: "",
      templateName: "appointment_missed",
      template: "",
      language: "en_US",
      variables: { 1: "{{first_name}}" },
      wait_for_response: true,
      wait_for_response_seconds: 86400,
      purpose: "appointment_missed",
    },
    0
  ),

  // step 1 (preset_2) — Condition: did they click Reschedule?
  //   TRUE  → step_3 (Reschedule branch)
  //   FALSE → step_6 (Cancel branch: farewell + agent + tag)
  makeActionNode(
    "Condition",
    {
      customerAttribute: "reply_text",
      condition: "contains",
      value: "reschedule",
      logic: "AND",
      waitTimeoutValue: 24,
      waitTimeoutUnit: "hours",
      wait_timeout_seconds: 86400,
      conditions: [
        { variable: "reply_text", operator: "contains", value: "reschedule" },
      ],
      true_next: "preset_3_whatsapp_message",
      false_next: "preset_6_whatsapp_message",
    },
    1
  ),

  // step 2 (preset_3) — Reschedule branch: appointment_reschedule with
  // Tomorrow/This week/Next week buttons. Waits for slot pick.
  makeActionNode(
    "WhatsApp Message",
    {
      channel: "WhatsApp",
      templateId: "",
      templateName: "appointment_reschedule",
      template: "",
      language: "en_US",
      variables: { 1: "{{first_name}}" },
      wait_for_response: true,
      wait_for_response_seconds: 86400,
      purpose: "appointment_reschedule",
      next: "preset_4_assign_agent",
      _branch: "reschedule",
    },
    2
  ),

  // step 3 (preset_4) — Reschedule branch: Assign Agent (slot pick).
  makeActionNode(
    "Assign Agent",
    {
      agentId: "",
      agentName: "",
      notes:
        "Customer missed the appointment, chose to reschedule — slot preference in last reply.",
      next: "preset_5_add_or_remove_tags",
      _branch: "reschedule",
    },
    3
  ),

  // step 4 (preset_5) — Reschedule branch: Tag RESCHEDULE REQUESTED.
  makeActionNode(
    "Add or Remove Tags",
    {
      tags: ["RESCHEDULE REQUESTED"],
      _branch: "reschedule",
      _terminal: true,
    },
    4
  ),

  // step 5 (preset_6) — Cancel branch: appointment_cancel_farewell
  // (goodbye + welcoming-future-bookings message per Scree 06-24).
  makeActionNode(
    "WhatsApp Message",
    {
      channel: "WhatsApp",
      templateId: "",
      templateName: "appointment_cancel_farewell",
      template: "",
      language: "en_US",
      variables: { 1: "{{first_name}}" },
      purpose: "appointment_cancel_farewell",
      next: "preset_7_assign_agent",
      _branch: "cancel",
    },
    5
  ),

  // step 6 (preset_7) — Cancel branch: Assign Agent.
  makeActionNode(
    "Assign Agent",
    {
      agentId: "",
      agentName: "",
      notes: "Customer missed appointment and cancelled — agent acknowledges closure.",
      next: "preset_8_add_or_remove_tags",
      _branch: "cancel",
    },
    6
  ),

  // step 7 (preset_8) — Cancel branch: Tag APPOINTMENT CANCELLED.
  makeActionNode(
    "Add or Remove Tags",
    {
      tags: ["APPOINTMENT CANCELLED"],
      _branch: "cancel",
      _terminal: true,
    },
    7
  ),
];

// Feedback Collection — 5 cards (Joseph 06-30: "remove create support
// ticket it only show when the even is ticket based"). The ticket-
// creation step only belongs in flows whose trigger event is itself
// ticket-related (e.g. On_Ticket_Closed). Default Feedback Collection
// preset drops it.
//
// Good/Average/Poor is a single card (feedback_request), not 3 separate
// cards (design 07-02: "you don't need to display 3 cards ... if I select
// good inside the same card at the bottom show the text"). The buttons
// under the Send Message card (SendMessageOut) are clickable — picking
// one shows that branch's reply text inline, under the same card.
//
// Canvas:
//   Trigger (On Event: Feedback_Collection_Start)
//     ↓
//   Set Delay Time         (0d 1h 0m 0s)
//     ↓
//   Send Message           (feedback_request, purpose=feedback_collection,
//                           Good/Average/Poor buttons — click one to preview
//                           its reply text under this same card)
//     ↓
//   Store Data             (Feedback == [reply], Contact_Profile)
//     ↓
//   Send Internal Notif    (Support Team, feedback_internal_escalation
//                           — fires only when reply was Poor)
//     ↓
//   Assign Agent
//
// 4 Meta templates (all APPROVED):
//   feedback_request               (Good/Average/Poor buttons)
//   feedback_thanks_good           (Good auto-reply via dispatcher)
//   feedback_suggestions           (Average + Poor auto-reply via dispatcher)
//   feedback_internal_escalation   (Support Team)
export const makeFeedbackCollectionJourneyActions = (): ActionNode[] => [
  // step 0 — Wait 1 hour after the triggering event.
  makeActionNode(
    "Set Delay Time",
    { days: 0, hours: 1, minutes: 0, seconds: 0 },
    0
  ),

  // step 1 — feedback_request. purpose=feedback_collection hands the
  // post-reply branching to the backend dispatcher (workflows.py):
  // Good → feedback_thanks_good + store; Average → feedback_suggestions
  // + store; Poor → feedback_suggestions (same reply as Average) + store
  // + send internal notification.
  makeActionNode(
    "Send Interactive Message",
    {
      channel: "WhatsApp",
      templateName: "feedback_request",
      language: "en",
      variables: { 1: "{{first_name}}" },
      purpose: "feedback_collection",
      wait_for_response: true,
      wait_for_response_seconds: 86400,
    },
    1
  ),

  // step 2 — Store Data. Three pre-configured rows on the same card
  // so the operator sees all 3 branches at once (Joseph 07-01: "Show
  // all 3 branches (Good/Average/Poor) as separate rows on the same
  // Store Data card, each pre-configured (Feedback == Good / Average
  // / Poor)"). Runtime routing is handled by the backend dispatcher
  // — it writes the exact "Feedback == <reply>" based on the
  // customer's tap, regardless of what the canvas rows read.
  makeActionNode(
    "Store Data",
    {
      rules: [
        { dataToStore: "Feedback == Good",    storeIn: "Contact_Profile" },
        { dataToStore: "Feedback == Average", storeIn: "Contact_Profile" },
        { dataToStore: "Feedback == Poor",    storeIn: "Contact_Profile" },
      ],
      // Kept for backend backwards compatibility — Celery task reads
      // config.dataToStore. First row is the primary.
      dataToStore: "Feedback == Good",
      storeIn:     "Contact_Profile",
    },
    2
  ),

  // step 3 — Send Internal Notification (Poor branch only at runtime).
  // Dispatcher only fires this when reply was Poor — pings Support
  // Team via WhatsApp using feedback_internal_escalation template.
  makeActionNode(
    "Send Internal Notification",
    {
      channel: "WhatsApp",
      team: "Support Team",
      templateName: "feedback_internal_escalation",
      message: "Hi everyone, please look into this on an urgent basis.",
    },
    3
  ),

  // step 4 — Assign Agent.
  makeActionNode(
    "Assign Agent",
    {
      agentId: "",
      agentName: "",
      notes: "This is a first time customer.",
    },
    4
  ),
];
// Appointment Reminder — single Send Interactive Message card per Joseph's 06-29 mockup.
//
// Canvas shows just 3 cards (after the Trigger):
//   1. Wait Until            (1 day before appointment_date)
//   2. Send Message          (ONE card — operator picks the template;
//                             the card preview changes based on the
//                             template they pick, including the
//                             customer-facing buttons preview)
//   3. Assign Agent          (picks from /v1/agents)
//
// Hardcoded in the BACKEND (workflows.py keyed on purpose=
// "appointment_reminder" — Joseph 06-25 simplification):
//   YES        → backend auto-sends appointment_confirmation +
//                continues to Assign Agent
//   NO         → backend auto-sends appointment_cancelled,
//                appointment_cancel_reason, appointment_assistance_handoff
//                (if "Need assistance" picked) + Assign Agent +
//                Tag APPOINTMENT CANCELLED
//   Reschedule → backend auto-sends appointment_reschedule + waits for
//                slot pick + Assign Agent + Tag RESCHEDULE REQUESTED
//   24h after appointment_date with no YES → backend fires
//                appointment_missed (Temporal timer)
//
// Operators don't see any of those branches on the canvas. They pick
// the Reminder template and that's it.
export const makeAppointmentReminderJourneyActions = (): ActionNode[] => [
  // step 0 — Wait until 1 day before the appointment.
  makeActionNode(
    "Wait Until",
    {
      waitMode: "date",
      dateAttribute: "appointment_date",
      offsetValue: 1,
      offsetUnit: "Days",
      offsetDirection: "Before",
    },
    0
  ),

  // step 1 — Send appointment_reminder (Sree 06-29 spec: no Fetch Audience
  // card; audience comes from the Appointment_Scheduled event itself). The
  // WhatsApp Message card carries the template picker + the operator's
  // branch-to-configure radios. Backend auto-dispatch (workflows.py with
  // purpose="appointment_reminder") handles YES → confirmation, NO →
  // cancelled + cancel_reason, Reschedule → reschedule template.
  makeActionNode(
    "WhatsApp Message",
    {
      channel: "WhatsApp",
      templateId: "",
      templateName: "appointment_reminder",
      template: "",
      language: "en",
      variables: { 1: "{{first_name}}" },
      purpose: "appointment_reminder",
      wait_for_response: true,
      wait_for_response_seconds: 86400,
    },
    1
  ),

  // step 2 — Assign Agent. agentId stays empty so journey author picks
  // from the /v1/agents dropdown.
  makeActionNode(
    "Assign Agent",
    {
      agentId: "",
      agentName: "",
      notes: "Customer reached out about their appointment.",
    },
    2
  ),
];

export const makeLeadQualificationJourneyActions = (): ActionNode[] => [
  makeActionNode(
    "Send Email",
    {
      templateName: "",
      template: "",
      subject: "",
      language: "en_US",
    },
    0
  ),
  makeActionNode(
    "Condition",
    {
      // signal-backed field → backend auto-waits up to wait_timeout_seconds
      customerAttribute: "email_event_type",
      condition: "equals",
      value: "opened",
      logic: "AND",
      // 2 days — matches realistic email-open window. User can override in the card.
      waitTimeoutValue: 2,
      waitTimeoutUnit: "days",
      wait_timeout_seconds: 172800,
      conditions: [
        { variable: "email_event_type", operator: "equals", value: "opened" },
      ],
      // True/False bullets pre-wired to the warm/cold cards below — this is what
      // makes the canvas draw both branch arrows automatically.
      true_next: PRESET_WARM_ID,
      false_next: PRESET_COLD_ID,
    },
    1
  ),
  makeActionNode(
    "WhatsApp Message",
    {
      // empty — user picks any WhatsApp template (e.g. lead_warm_nudge, intro_msg, etc)
      templateName: "",
      template: "",
      language: "en_US",
      channel: "WhatsApp",
      // {{1}} → first_name is a sensible default mapping for the first placeholder.
      // The card re-derives slot positions from the picked template's body.
      variables: { 1: "{{first_name}}" },
      _branch: "true",
    },
    2
  ),
  makeActionNode(
    "WhatsApp Message",
    {
      // empty — user picks any WhatsApp template (cold nudge, follow-up, etc)
      templateName: "",
      template: "",
      language: "en_US",
      channel: "WhatsApp",
      variables: { 1: "{{first_name}}" },
      _branch: "false",
    },
    3
  ),
];

export const makeBirthdayJourneyActions = (): ActionNode[] => [
  makeActionNode("Fetch Current Date", { dateSource: "current_date", dateFormat: "dd-mm-yyyy" }, 0),
  makeActionNode(
    "Fetch Audience",
    {
      dataSource: "csv",
      dateColumn: "Birthday_Date",
      dateFormat: "dd-mm-yyyy",
      templateName: "birthday_greet",
      language: "en_US",
    },
    1
  ),
  makeActionNode(
    "Send Birthday Message",
    {
      channel: "WhatsApp",
      templateId: "",
      templateName: "birthday_greet",
      language: "en_US",
      variables: {},
    },
    3
  ),

  makeActionNode(
    "Condition",
    {
      variant: "birthday",
      logic: "AND",
      conditions: [
        { variable: "Birthday_Date", operator: "matches_today" },
        { variable: "Last_wished_on", operator: "matches_today" },
        { variable: "WhatsApp_number", operator: "exists" },
        { variable: "Opt-in status", operator: "true" },
      ],
    },
    2
  ),
  makeActionNode(
    "Send Message Template",
    {
      template: "birthday_greet",
      templateName: "birthday_greet",
      language: "en_US",
      channel: "WhatsApp",
      variables: {},
    },
    3
  ),
  makeActionNode(
    "Mark as Sent",
    {
      dataSource: "csv",
      updateColumn: "Last_wished_on",
      updateValue: "Today",
    },
    4
  ),

  makeActionNode("Set Delay Time", {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  }, 5)
];

export const makeTriggerNode = (type: string): TriggerNode | null => {
  if (type === "On Message") {
    return {
      type: "On Message",
      keywords: [{ id: "kw_1", text: "", match_similar: false }],
      restart_journey: false,
    };
  }

  if (type === "New Lead") {
    return {
      type: "New Lead",
      source: "Manual",
    };
  }

  if (type === "On Event") {
    return {
      type: "On Event",
      eventType: "Appointment_Scheduled",
      restart_journey: true,
    };
  }

  if (type === "Run at Specific Time") {
    return {
      type: "Run at Specific Time",
      ...makeScheduledConfig(),
    };
  }

  if (type === "Send Message Template") {
    return {
      type: "Send Message Template",
      channel: "WhatsApp",
      templateId: "",
      sendTime: "now",
    };
  }

  if (type === "Lead from CTWA") {
    return { type: "Lead from CTWA", waba: "All WABA", trackLeadsFrom: "All Broadcasts" };
  }

  if (type === "RCS Trigger Node") {
    return {
      type: "RCS Trigger Node",
      keywords: [
        { id: "kw_1", text: "hi", match_similar: false },
        { id: "kw_2", text: "hello", match_similar: false },
      ],
      restart_journey: false,
    };
  }

  return null;
};